/** Shared vocabulary. Every string here matches a Postgres enum in the API schema. */

export const ROLES = ['admin', 'board', 'convenor', 'ice', 'bar', 'member'] as const;
export type Role = (typeof ROLES)[number];

/** Roles that may administer the whole club. */
export const ADMIN_ROLES: readonly Role[] = ['admin', 'board'];

export const MEMBER_STATUS = ['active', 'inactive', 'lapsed', 'deceased'] as const;
export type MemberStatus = (typeof MEMBER_STATUS)[number];

export const LEAGUE_FORMAT = [
  'skips_entry', // teams enter whole; fixed rosters for the schedule
  'draw_pool',   // individuals enter; teams redrawn each schedule
  'open_draw',   // teams drawn from whoever turns up (seniors)
  'stick',       // 2-on-2 stick curling
  'program',     // coached program rather than league play (Little Rock, Special Olympics)
] as const;
export type LeagueFormat = (typeof LEAGUE_FORMAT)[number];

export const LEAGUE_GENDER = ['open', 'women', 'men', 'mixed', 'youth'] as const;
export type LeagueGender = (typeof LEAGUE_GENDER)[number];

export const POSITION = ['skip', 'vice', 'second', 'lead', 'alternate'] as const;
export type Position = (typeof POSITION)[number];

export const ENTRY_MODE = ['team', 'single', 'couple'] as const;
export type EntryMode = (typeof ENTRY_MODE)[number];

export const DRAW_STATUS = ['scheduled', 'in_progress', 'complete', 'cancelled'] as const;
export type DrawStatus = (typeof DRAW_STATUS)[number];

export const GAME_STATUS = ['scheduled', 'in_progress', 'final', 'forfeit', 'cancelled'] as const;
export type GameStatus = (typeof GAME_STATUS)[number];

export const BONSPIEL_KIND = ['in_house', 'open'] as const;
export type BonspielKind = (typeof BONSPIEL_KIND)[number];

export const ENTRY_STATUS = ['pending', 'confirmed', 'waitlist', 'withdrawn'] as const;
export type EntryStatus = (typeof ENTRY_STATUS)[number];

export const EVENT_KIND = [
  'league', 'bonspiel', 'social', 'meeting', 'rental', 'anniversary', 'other',
] as const;
export type EventKind = (typeof EVENT_KIND)[number];

export const AUDIENCE = ['public', 'members', 'league'] as const;
export type Audience = (typeof AUDIENCE)[number];

export const RENTAL_STATUS = ['enquiry', 'held', 'confirmed', 'cancelled'] as const;
export type RentalStatus = (typeof RENTAL_STATUS)[number];

export const INVOICE_STATUS = ['draft', 'open', 'paid', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[number];

export const PAYMENT_METHOD = ['cash', 'cheque', 'card', 'etransfer', 'stripe', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHOD)[number];

export const CHARGE_KIND = [
  'membership', 'bonspiel', 'rental', 'bar', 'merch', 'donation', 'other',
] as const;
export type ChargeKind = (typeof CHARGE_KIND)[number];

export const DUTY_KIND = ['lunch', 'bar', 'ice', 'draw_master', 'host', 'other'] as const;
export type DutyKind = (typeof DUTY_KIND)[number];

export const DUTY_STATUS = ['assigned', 'confirmed', 'declined', 'swapped'] as const;
export type DutyStatus = (typeof DUTY_STATUS)[number];

export const DOC_CATEGORY = [
  'waiver', 'form', 'rules', 'minutes', 'policy', 'price_list', 'other',
] as const;
export type DocCategory = (typeof DOC_CATEGORY)[number];

export const SPONSOR_TIER = ['title', 'major', 'supporting', 'advertiser'] as const;
export type SponsorTier = (typeof SPONSOR_TIER)[number];

export const SPONSOR_TARGET = ['club', 'league', 'division', 'bonspiel', 'trophy'] as const;
export type SponsorTarget = (typeof SPONSOR_TARGET)[number];

export const DAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;
export type DayName = (typeof DAYS)[number];

/** 0 = Sunday, matching Postgres `extract(dow)` and JS `Date#getDay`. */
export function dayName(dow: number): DayName {
  return DAYS[((dow % 7) + 7) % 7] as DayName;
}

export const REGISTRATION_STATUS = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUS)[number];

export const PAYMENT_CHOICE = ['now', 'later'] as const;
export type PaymentChoice = (typeof PAYMENT_CHOICE)[number];

/** Who wrote a message. The office side is the club, not a named person. */
export const MESSAGE_AUTHOR = ['member', 'office'] as const;
export type MessageAuthor = (typeof MESSAGE_AUTHOR)[number];

export const THREAD_STATE = ['open', 'answered', 'closed'] as const;
export type ThreadState = (typeof THREAD_STATE)[number];

/* ------------------------------------------------- check-in & door access -- */

export const CHECKIN_METHOD = [
  'member_qr',   // the desk scanned the code on the member's phone
  'station_qr',  // the member scanned the printed code by the door
  'desk',        // an officer ticked them off by hand
  'door',        // inferred from a granted door event
] as const;
export type CheckinMethod = (typeof CHECKIN_METHOD)[number];

/** Human wording for a check-in method, for a log the office reads. */
export const CHECKIN_METHOD_LABEL: Record<CheckinMethod, string> = {
  member_qr: 'Scanned at the desk',
  station_qr: 'Scanned the door code',
  desk: 'Marked in by the office',
  door: 'Came through the door',
};

export const ACCESS_PROVIDER = ['manual', 'kisi', 'brivo'] as const;
export type AccessProvider = (typeof ACCESS_PROVIDER)[number];

export const ACCESS_CREDENTIAL_KIND = ['card', 'fob', 'mobile', 'pin'] as const;
export type AccessCredentialKind = (typeof ACCESS_CREDENTIAL_KIND)[number];

export const ACCESS_CREDENTIAL_STATE = [
  'pending',    // enrolled here, not yet pushed to the provider
  'active',
  'suspended',  // temporarily off; membership lapsed, waiver outstanding
  'lost',
  'revoked',
] as const;
export type AccessCredentialState = (typeof ACCESS_CREDENTIAL_STATE)[number];

export const ACCESS_DECISION = ['granted', 'denied', 'unknown'] as const;
export type AccessDecision = (typeof ACCESS_DECISION)[number];
